import { api, apiUpload } from './apiClient';
import { signalBadgesChanged } from './badgeSignal';

export type ReportCategory = 'feedback' | 'issue' | 'suggestion';
export type ReportIssueType = 'product' | 'profile' | 'location' | 'app' | 'user';
/**
 * Two states, not three. `dismissed` exists in the database as the admin's
 * junk filter and is never sent here — a report hidden that way simply stops
 * appearing in the list.
 */
export type ReportStatus = 'open' | 'resolved';

export const MAX_REPORT_PHOTOS = 3;

/** A picked message in the reporter's own complaint; all-null once it was purged. */
export type ReportedMessage =
  | { from: 'me' | 'them'; content: string; sentAt: string }
  | { from: null; content: null; sentAt: null };

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/rest/v1';

interface ApiReport {
  id: number;
  type: ReportCategory;
  issueType: ReportIssueType | null;
  description: string;
  status: ReportStatus;
  createdAt: string;
  answer: { note: string; answeredAt: string } | null;
  photos: { id: number; objectKey: string }[];
  target?: { name: string | null } | null;
  messages?: ReportedMessage[];
  listing?: { title: string | null } | null;
}

export interface Report {
  id: string;
  category: ReportCategory;
  issueType: ReportIssueType | null;
  description: string;
  status: ReportStatus;
  createdAt: string;
  answer: { note: string; answeredAt: string } | null;
  photos: { id: string; url: string }[];
  /** A user complaint's person, named as the chat names them; null for other reports. */
  target: { name: string | null } | null;
  messages: ReportedMessage[];
  /** A Hatalı İlan report's listing; title null once the listing is deleted. */
  listing: { title: string | null } | null;
}

/**
 * Wire ids are numeric; the rest of the app models ids as strings, so this
 * module is the seam that converts — same pattern as listingService.
 *
 * Screenshot URLs are built here rather than with assetUrl: these are NOT
 * served by the public /photos route, because a bug screenshot can show the
 * reporter's own account screen. Each one is checked per report and per photo.
 */
const toReport = (r: ApiReport): Report => ({
  id: String(r.id),
  category: r.type,
  issueType: r.issueType,
  description: r.description,
  status: r.status,
  createdAt: r.createdAt,
  answer: r.answer,
  photos: r.photos.map((p) => ({
    id: String(p.id),
    url: `${API_BASE}/reports/${r.id}/photos/${p.id}`,
  })),
  // Only the list sends these; a freshly filed report has neither.
  target: r.target ?? null,
  messages: r.messages ?? [],
  listing: r.listing ?? null,
});

export async function submitReport(input: {
  category: ReportCategory;
  issueType?: ReportIssueType | null;
  description: string;
  files: File[];
  listingId?: string | null;
}): Promise<Report> {
  const form = new FormData();
  form.append('type', input.category);
  if (input.issueType) form.append('issueType', input.issueType);
  form.append('description', input.description);
  if (input.listingId) form.append('listingId', input.listingId);
  for (const file of input.files) form.append('photos', file);

  return toReport(await apiUpload<ApiReport>('/reports', form));
}

export async function getMyReports(status?: ReportStatus): Promise<Report[]> {
  const query = status ? `?status=${status}` : '';
  return (await api<ApiReport[]>(`/reports/mine${query}`)).map(toReport);
}

export async function getUnseenAnswerCount(): Promise<number> {
  return (await api<{ count: number }>('/reports/mine/unseen-count')).count;
}

export async function markAnswersSeen(): Promise<void> {
  await api<void>('/reports/mine/seen', { method: 'PUT' });
  signalBadgesChanged();
}
